import React from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router"

import { Page404 } from "@/components/nav/page404"
import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import { useActiveOrganizationId, useAuthentication } from "@/contexts"
import { useAdminBillPageState } from "@/modules/admin/bills"
import { AdminEntityFieldsSkeleton } from "@/modules/admin/components/admin-entity-fields-skeleton"
import { useUrl } from "@/utils/url"

import { Button, Link as MuiLink, Stack } from "@mui/material"

import { AdminBillEditForm } from "./admin-bill-edit-form"

export const AdminBillEditRoute: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation("common")
  const { activeOrganizationId } = useActiveOrganizationId()
  const { isAdminWriterUser, isOrgAdminUser } = useAuthentication()
  const canEditBillFields = isAdminWriterUser || isOrgAdminUser
  const { adminBillPath, adminBillsPath } = useUrl()
  const { billData, billId, billIsFetching, billIsLoading, billIsUnavailable } =
    useAdminBillPageState()

  const organizationId = activeOrganizationId ?? ""

  if (!organizationId) {
    return null
  }

  if (billIsUnavailable) {
    return <Page404 />
  }

  if (billIsLoading || !billData || billId === undefined) {
    return (
      <Stack sx={{ gap: 2 }}>
        <PageTitle
          breadcrumbActions={
            <>
              <MuiLink
                component={Link}
                to={`${adminBillsPath}${location.search}`}
                underline="hover"
              >
                {t("admin.bills")}
              </MuiLink>
              {billId !== undefined ? (
                <MuiLink
                  component={Link}
                  to={`${adminBillPath(String(billId))}${location.search}`}
                  underline="hover"
                >
                  {t("admin.billDetail.viewBill")}
                </MuiLink>
              ) : null}
            </>
          }
          heading={t("editBill.title")}
          primaryActions={
            <Button
              onClick={() => {
                void navigate(
                  `${adminBillPath(String(billId))}${location.search}`,
                  {
                    replace: true,
                  }
                )
              }}
              variant="outlined"
            >
              {t("button.cancel")}
            </Button>
          }
        />
        <PageCard>
          <AdminEntityFieldsSkeleton
            ariaLabel={t("loadingAriaLabel", {
              title: t("admin.billDetail.title"),
            })}
            roundedInputs
          />
        </PageCard>
      </Stack>
    )
  }

  return (
    <AdminBillEditForm
      key={billId}
      bill={billData}
      billId={billId}
      billIsFetching={billIsFetching}
      canEditBillFields={canEditBillFields}
      organizationId={organizationId}
    />
  )
}
